import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import dayjs from "dayjs";
import { IoTimeOutline } from "react-icons/io5";
import { Link } from "react-router";
import styled from "styled-components";

import ListRecentBimClassesAndGeometryTypes from "./ListRecentBimClassesAndGeometryTypes.graphql";
import { TAXONOMY_BASE } from "../../../../constants";

const StyledRecentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    .section-title {
        align-items: center;
        color: #172b4d;
        display: flex;
        font-size: 0.95rem;
        font-weight: 600;
        gap: 8px;

        svg {
            color: #6b778c;
            font-size: 1.1rem;
        }
    }

    ul {
        display: flex;
        flex-direction: column;
        gap: 8px;
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .recent-item {
        align-items: center;
        background: #fff;
        border: 1px solid #ebecf0;
        border-radius: 8px;
        display: flex;
        justify-content: space-between;
        padding: 12px 16px;
        text-decoration: none;
        transition: background 0.15s, border-color 0.15s, transform 0.15s, box-shadow 0.15s;

        &:hover {
            background: #fafbfc;
            border-color: #0052cc;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
            transform: translateY(-1px);
        }

        .item-main {
            align-items: center;
            display: flex;
            gap: 12px;
        }

        .badge {
            border-radius: 4px;
            font-size: 0.7rem;
            font-weight: 700;
            letter-spacing: 0.03em;
            padding: 3px 6px;
            text-transform: uppercase;

            &.bc {
                background: #e3fcef;
                color: #006644;
            }
            &.gt {
                background: #eae6ff;
                color: #403294;
            }
        }

        .item-name {
            color: #172b4d;
            font-size: 0.95rem;
            font-weight: 500;
        }

        .item-parent {
            color: #6b778c;
            font-size: 0.85rem;
        }

        time {
            color: #8993a4;
            font-size: 0.8rem;
            white-space: nowrap;
        }
    }
`;

export default function RecentBimClassesAndGeometryTypes() {
    const listRecentBimClassesAndGeometryTypes = useQuery(gql(ListRecentBimClassesAndGeometryTypes));

    if (listRecentBimClassesAndGeometryTypes.loading) return <div>Loading recent activity...</div>;
    if (listRecentBimClassesAndGeometryTypes.error) return <div>Failed to load recent activity.</div>;

    const recent = [];

    for (const bimClass of listRecentBimClassesAndGeometryTypes.data?.recentBimClasses ?? []) {
        recent.push(
            {
                id: bimClass.id,
                revisionID: bimClass.revisionID,
                name: bimClass.data.name[0].text,
            }
        );
    }
    for (const geometryType of listRecentBimClassesAndGeometryTypes.data?.recentGeometryTypes ?? []) {
        recent.push(
            {
                id: geometryType.id,
                revisionID: geometryType.revisionID,
                name: geometryType.name[0].text,
                bimClass: geometryType.bimClass,
            }
        );
    }

    return (
        <StyledRecentContainer>
            <div className="section-title">
                <IoTimeOutline />
                <span>Recently Edited</span>
            </div>
            <ul>
                {
                    recent.sort((a, b) => b.revisionID.localeCompare(a.revisionID)).slice(0, 10).map(
                        (item) => (
                            <li key={ item.id }>
                                <Link
                                    className="recent-item"
                                    to={ `${ item.bimClass ? `${ TAXONOMY_BASE }/BimClasses/${ item.bimClass.id }` : "" }/${ item.bimClass ? "GeometryTypes" : "BimClasses" }/${ item.id }` }
                                >
                                    <div className="item-main">
                                        <span className={ `badge ${ item.bimClass ? "gt" : "bc" }` }>
                                            { item.bimClass ? "GT" : "BC" }
                                        </span>
                                        <span className="item-name">{ item.name }</span>
                                        {
                                            item.bimClass && (
                                                <span className="item-parent">
                                                    (BimClass: { item.bimClass.data.name[0].text })
                                                </span>
                                            )
                                        }
                                    </div>
                                    <time>
                                        { dayjs(parseInt(item.revisionID.substring(0, 8), 16) * 1000).format("MMM D, YYYY HH:mm") }
                                    </time>
                                </Link>
                            </li>
                        )
                    )
                }
            </ul>
        </StyledRecentContainer>
    );
}
